import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  NotificationService,
  type NotificationListParams,
  type UnreadCountResponse,
} from '@/services/notificationService';
import { QUERY_KEYS } from '@/constants';
import { useInfiniteList } from '@/hooks/useInfiniteList';

export function useInboxNotifications(params?: NotificationListParams) {
  return useInfiniteList({
    queryKey: [...QUERY_KEYS.notifications, { ...params, page: undefined }],
    queryFn: (page) => NotificationService.list({ ...params, page }),
    pageSize: params?.limit,
  });
}

export function useNotificationUnreadCount() {
  return useQuery({
    queryKey: QUERY_KEYS.notificationUnread,
    queryFn: async (): Promise<UnreadCountResponse> => {
      const res = await NotificationService.unreadCount();
      return (
        res.data ?? {
          count: 0,
          total: 0,
          unread: 0,
          emergencyUnread: 0,
          financeUnread: 0,
          importantCount: 0,
        }
      );
    },
    refetchInterval: 30_000,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => NotificationService.markRead(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notifications });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notificationUnread });
    },
  });
}

export function useToggleNotificationImportant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => NotificationService.toggleImportant(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notifications });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notificationUnread });
    },
  });
}

export function useAcknowledgeNotificationEmergency() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => NotificationService.acknowledgeEmergency(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notifications });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notificationUnread });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (category?: string) => NotificationService.markAllRead(category),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notifications });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notificationUnread });
    },
  });
}

export function useClearAllNotifications() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => NotificationService.clearAll(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notifications });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notificationUnread });
    },
  });
}

